import { Link, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, 
  Package, 
  FolderOpen, 
  Truck, 
  ClipboardList, 
  BadgePercent,
  PanelsTopLeft,
  CreditCard,
  LogOut,
  Menu,
  X,
  Users,
  UserCircle,
  ChevronDown,
} from "lucide-react";
import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import BrandLogo from "./BrandLogo";

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const userRole = user?.role || "admin";

  // Navigation items with role-based access
  const allNavigation = [
    {
      name: "Dashboard",
      href: "/admin/dashboard",
      icon: LayoutDashboard,
      roles: ["superadmin", "owner", "admin"],
    },
    {
      name: "Produk",
      href: "/admin/produk",
      icon: Package,
      roles: ["superadmin", "owner", "admin"],
    },
    {
      name: "Kategori",
      href: "/admin/kategori",
      icon: FolderOpen,
      roles: ["superadmin", "owner"],
    },
    {
      name: "Kurir",
      href: "/admin/kurir",
      icon: Truck,
      roles: ["superadmin", "owner", "admin"],
    },
    {
      name: "Pembayaran",
      href: "/admin/pembayaran",
      icon: CreditCard,
      roles: ["superadmin", "owner"],
    },
    {
      name: "Penjualan",
      href: "/admin/penjualan",
      icon: ClipboardList,
      roles: ["superadmin", "owner", "admin"],
    },
    {
      name: "Kupon",
      href: "/admin/kupon",
      icon: BadgePercent,
      roles: ["superadmin", "owner", "admin"],
    },
    {
      name: "LP Builder",
      href: "/admin/landing-pages",
      icon: PanelsTopLeft,
      roles: ["superadmin", "owner"],
    },
    {
      name: "Manajemen User",
      href: "/admin/users",
      icon: Users,
      roles: ["superadmin"],
    },
  ];

  const navigation = allNavigation
    .filter((item) => item.roles.includes(userRole))
    .map((item) => ({
      ...item,
      current: location.pathname === item.href,
    }));

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const ROLE_LABELS = {
    superadmin: "Superadmin",
    admin: "Admin",
    owner: "Owner",
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-stone-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col bg-white shadow-xl transform transition-transform duration-300 ease-in-out lg:hidden ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex items-center justify-between h-16 px-4 border-b border-stone-200">
          <div className="flex items-center">
            <BrandLogo className="h-9 w-9" padded rounded="rounded-lg" />
            <span className="ml-2 text-lg font-semibold text-stone-900">Admin Panel</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-1 rounded-md text-stone-400 hover:text-stone-500 hover:bg-stone-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="mt-5 flex-1 overflow-y-auto px-2 pb-4">
          {navigation.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md mb-1 ${
                  item.current
                    ? 'bg-emerald-100 text-emerald-900'
                    : 'text-stone-600 hover:bg-stone-50 hover:text-stone-900'
                }`}
              >
                <Icon className={`mr-3 flex-shrink-0 h-5 w-5 ${
                  item.current ? 'text-emerald-500' : 'text-stone-400 group-hover:text-stone-500'
                }`} />
                {item.name}
              </Link>
            );
          })}
        </nav>
        {/* Mobile user section */}
        <div className="p-4 border-t border-stone-200">
          <Link
            to="/admin/profil"
            onClick={() => setSidebarOpen(false)}
            className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-stone-50 mb-2"
          >
            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
              <span className="text-sm font-medium text-emerald-700">
                {user?.name?.charAt(0)?.toUpperCase() || "U"}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-stone-900 truncate">{user?.name}</p>
              <p className="text-xs text-stone-500">{ROLE_LABELS[userRole]}</p>
            </div>
          </Link>
          <button
            onClick={handleLogout}
            className="flex items-center w-full px-2 py-2 text-sm font-medium text-stone-600 rounded-md hover:bg-stone-50 hover:text-stone-900"
          >
            <LogOut className="mr-3 h-5 w-5 text-stone-400" />
            Logout
          </button>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex min-h-0 flex-1 flex-col bg-white shadow-lg">
          <div className="flex h-16 flex-shrink-0 items-center px-4 border-b border-stone-200">
            <BrandLogo className="h-9 w-9" padded rounded="rounded-lg" />
            <span className="ml-2 text-lg font-semibold text-stone-900">Admin Panel</span>
          </div>
          <div className="flex flex-1 flex-col overflow-y-auto">
            <nav className="flex-1 px-2 py-4">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md mb-1 ${
                      item.current
                        ? 'bg-emerald-100 text-emerald-900'
                        : 'text-stone-600 hover:bg-stone-50 hover:text-stone-900'
                    }`}
                  >
                    <Icon className={`mr-3 flex-shrink-0 h-5 w-5 ${
                      item.current ? 'text-emerald-500' : 'text-stone-400 group-hover:text-stone-500'
                    }`} />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
            {/* Desktop user section */}
            <div className="flex-shrink-0 p-4 border-t border-stone-200">
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-3 w-full px-2 py-2 rounded-md hover:bg-stone-50 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                    <span className="text-sm font-medium text-emerald-700">
                      {user?.name?.charAt(0)?.toUpperCase() || "U"}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-medium text-stone-900 truncate">{user?.name}</p>
                    <p className="text-xs text-stone-500">{ROLE_LABELS[userRole]}</p>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                {userMenuOpen && (
                  <div className="absolute bottom-full left-0 right-0 mb-1 bg-white border border-stone-200 rounded-xl shadow-lg overflow-hidden">
                    <Link
                      to="/admin/profil"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-stone-700 hover:bg-stone-50 transition-colors"
                    >
                      <UserCircle className="w-4 h-4 text-stone-400" />
                      Pengaturan Profil
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-stone-700 hover:bg-stone-50 transition-colors"
                    >
                      <LogOut className="w-4 h-4 text-stone-400" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64 flex flex-col flex-1">
        {/* Top bar for mobile */}
        <div className="sticky top-0 z-10 flex h-16 flex-shrink-0 bg-white shadow-sm lg:hidden">
          <button
            type="button"
            className="border-r border-stone-200 px-4 text-stone-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-emerald-500 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex flex-1 justify-between px-4">
            <div className="flex flex-1 items-center">
              <h1 className="text-lg font-semibold text-stone-900">Admin Panel</h1>
            </div>
            <div className="ml-4 flex items-center md:ml-6">
              <button
                onClick={handleLogout}
                className="rounded-full bg-white p-1 text-stone-400 hover:text-stone-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
